/**
 * Catalog — Globální registry sdílených dat
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../../lib/i18n';
import { api } from '../../lib/api';
import { BackButton } from '../../components/ui/BackButton';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { EmptyState } from '../../components/ui/EmptyState';
import { InlineActions } from '../../components/ui/InlineActions';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui';
import {
  ChevronRight,
  Clock,
  Cpu,
  Database,
  Download,
  Globe,
  HardDrive,
  Layers,
  Mail,
  Network,
  Plus,
  ShieldCheck,
  Tag,
  Upload,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Registry {
  id: string;
  label: string;
  description: string;
  acl_read: string;
  acl_write: string;
  default_ttl: number | null;
  mqtt_retain?: boolean;
  icon: string;
  created_at?: string;
  updated_at?: string;
  entry_count: string;
}

interface Entry {
  /** Composite stable id (MCP list nemusí vracet UUID) */
  id: string;
  key: string;
  value: unknown;
  node_scope: string;
  source: string;
  ttl: number | null;
  expires_at: string | null;
  created_at?: string;
  updated_at?: string;
}

function entryId(e: { key: string; node_scope?: string; id?: string }) {
  return e.id || `${e.key}::${e.node_scope || 'global'}`;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ICONS: Record<string, React.ElementType> = {
  globe: Globe,
  'hard-drive': HardDrive,
  network: Network,
  cpu: Cpu,
  mail: Mail,
  'shield-check': ShieldCheck,
  database: Database,
};

function RegIcon({ name, size = 16 }: { name: string; size?: number }) {
  const Ic = ICONS[name] ?? Database;
  return <Ic size={size} />;
}

function AclBadge({ acl }: { acl: string }) {
  if (acl === 'public') return <Badge variant='green'>public</Badge>;
  if (acl === 'admin') return <Badge variant='red'>admin</Badge>;
  if (acl === 'node') return <Badge variant='blue'>node</Badge>;
  return <Badge variant='yellow'>{acl}</Badge>;
}

function TtlBadge({ ttl }: { ttl: number | null }) {
  if (!ttl) return <Badge variant='gray'>∞</Badge>;
  if (ttl < 3600) return <Badge variant='yellow'>{ttl}s</Badge>;
  if (ttl < 86400) return <Badge variant='yellow'>{Math.round(ttl / 3600)}h</Badge>;
  return <Badge variant='gray'>{Math.round(ttl / 86400)}d</Badge>;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtExpiry(iso: string | null) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return i18n.t('catalog.expired');
  if (diff < 60_000) return `${Math.round(diff / 1000)}s`;
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}min`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h`;
  return `${Math.round(diff / 86_400_000)}d`;
}

// ─── SimpleTabs ───────────────────────────────────────────────────────────────

type View = { type: 'registries' } | { type: 'entries'; registry: Registry };

/**
 * Zdroj = capability. Uzel se tady NEUKAZUJE jako uzel — jen jako UUID, kterému
 * je zdroj delegovaný, plus stav cesty (Filip 2026-08-25: „routuje si to na něj
 * zdroje, je mi jedno, kde v topologii je, je to jen resources").
 */
interface Resource {
  key: string;
  kind: string | null;
  qualifier: string | null;
  module: string | null;
  provider_type: string | null;
  ha: string | null;
  ha_reason: string | null;
  primary: boolean;
  priority: number;
  enabled: boolean;
  delegated_to: string | null;
  delegated_to_label: string | null;
  delegated_to_is_self: boolean;
  tenant_id: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
  assigned: boolean;
  route_status: string | null;
  route_transport: string | null;
  route_via: string | null;
  route_rtt_band: string | null;
  contracts: Array<{ key: string; scope: unknown }>;
  updated_at: string;
}

interface TenantOption {
  id: string;
  slug: string;
  name: string;
}

// ─── RegistryForm ─────────────────────────────────────────────────────────────

const REG_DEFAULTS = {
  id: '',
  label: '',
  description: '',
  acl_read: 'public',
  acl_write: 'admin',
  default_ttl: null as number | null,
  mqtt_retain: true,
  icon: 'database',
};

function RegistryForm({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: typeof REG_DEFAULTS | null;
  onClose: () => void;
  onSave: (d: typeof REG_DEFAULTS) => Promise<void>;
}) {
  const { t } = useTranslation();
  const isEdit = !!initial?.id;
  const [f, setF] = useState(initial ?? REG_DEFAULTS);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof typeof REG_DEFAULTS>(k: K, v: (typeof REG_DEFAULTS)[K]) =>
    setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.id || !f.label) {
      toast.error(t('catalog.requiredIdName'));
      return;
    }
    setSaving(true);
    try {
      await onSave(f);
      onClose();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t('catalog.editRegistry') : t('catalog.newRegistry')}
      size='lg'
      footer={
        <>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant='primary' onClick={submit} disabled={saving}>
            {saving ? t('catalog.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              ID <span className='text-red-400'>*</span>
            </label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
              value={f.id}
              onChange={(e) => set('id', e.target.value)}
              placeholder='network'
              disabled={isEdit}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              {t('catalog.nameLabel')} <span className='text-red-400'>*</span>
            </label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
              value={f.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder='Network'
            />
          </div>
        </div>

        <div>
          <label className='block text-xs text-gray-400 mb-1'>{t('catalog.descriptionLabel')}</label>
          <input
            className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
            value={f.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder={t('catalog.phRegistryDesc')}
          />
        </div>

        <div className='grid grid-cols-3 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>{t('catalog.aclRead')}</label>
            <Select
              className='w-full'
              value={f.acl_read}
              onChange={(e) => set('acl_read', e.target.value)}
            >
              <option value='public'>public</option>
              <option value='admin'>admin</option>
              <option value='node'>node</option>
              <option value='group:ops'>group:ops</option>
            </Select>
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>{t('catalog.aclWrite')}</label>
            <Select
              className='w-full'
              value={f.acl_write}
              onChange={(e) => set('acl_write', e.target.value)}
            >
              <option value='admin'>admin</option>
              <option value='node'>node</option>
              <option value='public'>public</option>
              <option value='group:ops'>group:ops</option>
            </Select>
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>{t('catalog.defaultTtl')}</label>
            <input
              type='number'
              min={0}
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
              value={f.default_ttl ?? ''}
              placeholder='∞'
              onChange={(e) => set('default_ttl', e.target.value ? parseInt(e.target.value) : null)}
            />
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>{t('catalog.icon')}</label>
            <Select
              className='w-full'
              value={f.icon}
              onChange={(e) => set('icon', e.target.value)}
            >
              {Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
            </Select>
          </div>
          <div className='flex items-end pb-2'>
            <label className='flex items-center gap-2 cursor-pointer'>
              <input
                type='checkbox'
                className='w-4 h-4 accent-blue-500'
                checked={f.mqtt_retain}
                onChange={(e) => set('mqtt_retain', e.target.checked)}
              />
              <span className='text-sm text-gray-300'>MQTT retain</span>
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── EntryForm ────────────────────────────────────────────────────────────────

const ENTRY_DEFAULTS = {
  key: '',
  value: '',
  node_scope: 'global',
  source: '',
  ttl: null as number | null,
};

function EntryForm({
  open,
  initial,
  registry,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: typeof ENTRY_DEFAULTS | null;
  registry: Registry;
  onClose: () => void;
  onSave: (d: typeof ENTRY_DEFAULTS) => Promise<void>;
}) {
  const { t } = useTranslation();
  const isEdit = !!initial?.key;
  const [f, setF] = useState(initial ?? { ...ENTRY_DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [valueStr, setValueStr] = useState(
    initial?.value ? JSON.stringify(initial.value, null, 2) : '',
  );

  const set = <K extends keyof typeof ENTRY_DEFAULTS>(k: K, v: (typeof ENTRY_DEFAULTS)[K]) =>
    setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.key) {
      toast.error(t('catalog.requiredKey'));
      return;
    }
    let parsedValue: unknown = valueStr;
    try {
      parsedValue = JSON.parse(valueStr);
    } catch { /* string value */ }
    setSaving(true);
    try {
      await onSave({ ...f, value: parsedValue as string });
      onClose();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t('catalog.editEntry') : t('catalog.newEntry')}
      size='lg'
      footer={
        <>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant='primary' onClick={submit} disabled={saving}>
            {saving ? t('catalog.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              {t('catalog.keyLabel')} <span className='text-red-400'>*</span>
            </label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-blue-500'
              value={f.key}
              onChange={(e) => set('key', e.target.value)}
              placeholder='acm1/public_ip'
              disabled={isEdit}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>Scope</label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
              value={f.node_scope}
              onChange={(e) => set('node_scope', e.target.value)}
              placeholder='global'
            />
          </div>
        </div>

        <div>
          <label className='block text-xs text-gray-400 mb-1'>
            {t('catalog.valueJson')}
          </label>
          <textarea
            rows={4}
            className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-blue-500 resize-none'
            value={valueStr}
            onChange={(e) => setValueStr(e.target.value)}
            placeholder={t('catalog.phValue')}
          />
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              {t('catalog.sourceLabel')}
            </label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
              value={f.source}
              onChange={(e) => set('source', e.target.value)}
              placeholder={t('catalog.phSource')}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              {t('catalog.ttlWithDefault', { default: registry.default_ttl ?? '∞' })}
            </label>
            <input
              type='number'
              min={0}
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
              value={f.ttl ?? ''}
              placeholder={`${registry.default_ttl ?? '∞'}`}
              onChange={(e) => set('ttl', e.target.value ? parseInt(e.target.value) : null)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── RegistriesView ───────────────────────────────────────────────────────────

function RegistriesView({
  registries,
  onSelect,
}: {
  registries: Registry[];
  onSelect: (r: Registry) => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<Registry | null>(null);
  const [formKey, setFormKey] = useState(0);

  // Registry CRUD + export/import — MCP (catalog.registries / export / import)
  const save = useMutation({
    mutationFn: async (d: typeof REG_DEFAULTS) =>
      api.mcp('catalog.registries', { action: 'upsert', ...d }),
    onSuccess: () => {
      toast.success(t('catalog.registrySaved'));
      qc.invalidateQueries({ queryKey: ['catalog-registries'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      api.mcp('catalog.registries', { action: 'delete', id }),
    onSuccess: () => {
      toast.success(t('catalog.registryDeleted'));
      qc.invalidateQueries({ queryKey: ['catalog-registries'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const doExport = async () => {
    try {
      const data = await api.mcp('catalog.export', {});
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `catalog-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('catalog.exportDownloaded'));
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const doImport = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text) as {
          registries?: unknown[];
          entries?: unknown[];
        };
        const res = await api.mcp<{ imported?: number; registries?: number }>(
          'catalog.import',
          {
            registries: data.registries ?? [],
            entries: data.entries ?? [],
          },
        );
        toast.success(t('catalog.importResult', { entries: res.imported, registries: res.registries }));
        qc.invalidateQueries({ queryKey: ['catalog-registries'] });
      } catch (e: unknown) {
        toast.error((e as Error).message);
      }
    };
    input.click();
  };

  const openEdit = (r: Registry) => {
    setEditItem(r);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  };
  const openNew = () => {
    setEditItem(null);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  };

  const totalEntries = registries.reduce((s, r) => s + parseInt(r.entry_count || '0'), 0);

  // Mesh-wide vs per-node rozdělení. Per-node = registry se source_of_truth obsahujícím
  // 'node' nebo id patřící mezi tradičně per-node (ports, storage).
  const PER_NODE_IDS = new Set(['storage', 'ports', 'cache']);
  const meshRegistries = registries.filter((r) => !PER_NODE_IDS.has(r.id));
  const perNodeRegistries = registries.filter((r) => PER_NODE_IDS.has(r.id));

  const renderCard = (r: Registry) => (
    <Card
      key={r.id}
      className='group cursor-pointer hover:border-gray-600 transition-colors'
      onClick={() => onSelect(r)}
    >
      <CardContent className='p-4'>
        <div className='flex items-start gap-3'>
          <div className='w-9 h-9 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 flex-shrink-0'>
            <RegIcon name={r.icon} size={16} />
          </div>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2 flex-wrap'>
              <span className='font-medium text-sm text-white'>{r.label}</span>
              <span className='text-xs text-gray-600 font-mono'>{r.id}</span>
              <Badge variant='gray'>
                <Layers size={9} className='mr-0.5 inline' />
                {r.entry_count}
              </Badge>
            </div>
            {r.description && (
              <p className='text-xs text-gray-600 mt-0.5 truncate'>{r.description}</p>
            )}
            <div className='flex items-center gap-2 mt-1.5 flex-wrap'>
              <span className='text-[10px] text-gray-600'>{t('catalog.readLabel')}</span>
              <AclBadge acl={r.acl_read} />
              <span className='text-[10px] text-gray-600'>{t('catalog.writeLabel')}</span>
              <AclBadge acl={r.acl_write} />
              <span className='text-[10px] text-gray-600'>TTL:</span>
              <TtlBadge ttl={r.default_ttl} />
              {r.mqtt_retain && <Badge variant='purple'>MQTT retain</Badge>}
            </div>
          </div>
          <div className='flex items-center gap-1 flex-shrink-0'>
            <InlineActions.Row
              onEdit={(e) => {
                e.stopPropagation();
                openEdit(r);
              }}
              onDelete={() => del.mutate(r.id)}
              confirmDelete={t('catalog.deleteRegistryConfirm', { label: r.label })}
            />
            <ChevronRight size={14} className='text-gray-600 ml-1' />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
      <div className='flex items-center justify-between mb-4'>
        <span className='text-sm text-gray-500'>
          {t('catalog.registriesSummary', { count: registries.length, entries: totalEntries })}
        </span>
        <div className='flex gap-2'>
          <Button variant='secondary' onClick={doImport}>
            <Upload size={14} className='mr-1' />Import
          </Button>
          <Button variant='secondary' onClick={doExport}>
            <Download size={14} className='mr-1' />Export
          </Button>
          <Button variant='primary' onClick={openNew}>
            <Plus size={14} className='mr-1' />{t('catalog.newRegistry')}
          </Button>
        </div>
      </div>

      {meshRegistries.length > 0 && (
        <div className='mb-6'>
          <div className='flex items-center gap-2 mb-2'>
            <Globe size={14} className='text-blue-400' />
            <h3 className='text-xs font-semibold uppercase tracking-wider text-gray-400'>
              {t('catalog.meshTitle')}
            </h3>
            <span className='text-[10px] text-gray-600'>
              {t('catalog.meshHint')}
            </span>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            {meshRegistries.map(renderCard)}
          </div>
        </div>
      )}

      {perNodeRegistries.length > 0 && (
        <div className='mb-6'>
          <div className='flex items-center gap-2 mb-2'>
            <HardDrive size={14} className='text-amber-400' />
            <h3 className='text-xs font-semibold uppercase tracking-wider text-gray-400'>
              {t('catalog.perNodeTitle')}
            </h3>
            <span className='text-[10px] text-gray-600'>
              {t('catalog.perNodeHint')}
            </span>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            {perNodeRegistries.map(renderCard)}
          </div>
        </div>
      )}

      {!registries.length && (
        <EmptyState
          icon={<Database size={32} className='text-gray-600' />}
          title={t('catalog.noRegistries')}
          description={t('catalog.noRegistriesDesc')}
          action={
            <Button variant='primary' onClick={openNew}>
              <Plus size={14} className='mr-1' />{t('catalog.newRegistry')}
            </Button>
          }
        />
      )}

      <RegistryForm
        key={formKey}
        open={formOpen}
        initial={editItem ?? null}
        onClose={() => setFormOpen(false)}
        onSave={(d) => save.mutateAsync(d)}
      />
    </>
  );
}

// ─── EntriesView ──────────────────────────────────────────────────────────────

function EntriesView({
  registry,
  onBack,
}: {
  registry: Registry;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const qKey = ['catalog-entries', registry.id];

  const { data } = useQuery({
    queryKey: qKey,
    queryFn: async () => {
      const res = await api.mcp<{
        entries?: Array<Omit<Entry, 'id'> & { id?: string }>;
      }>('catalog.list', { registry_id: registry.id }, { silent: true });
      return {
        entries: (res.entries ?? []).map((e) => ({
          ...e,
          id: entryId(e),
          source: e.source ?? '',
          node_scope: e.node_scope ?? 'global',
        })),
      };
    },
    refetchInterval: registry.default_ttl
      ? Math.min((registry.default_ttl * 1000) / 2, 30_000)
      : 30_000,
  });

  const entries: Entry[] = data?.entries ?? [];

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<Entry | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const save = useMutation({
    mutationFn: async (d: typeof ENTRY_DEFAULTS) => {
      // EntryForm už value parsuje z JSON textarey (object | string | number | …)
      return api.mcp('catalog.set', {
        registry_id: registry.id,
        key: d.key,
        value: d.value as unknown as Record<string, unknown>,
        scope: d.node_scope || 'global',
        source: d.source || '',
        ...(d.ttl != null ? { ttl: d.ttl } : {}),
      });
    },
    onSuccess: () => {
      toast.success(t('catalog.entrySaved'));
      qc.invalidateQueries({ queryKey: qKey });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: ({ key, scope }: { key: string; scope: string }) =>
      api.mcp('catalog.delete', {
        registry_id: registry.id,
        key,
        scope: scope || 'global',
      }),
    onSuccess: () => {
      toast.success(t('catalog.entryDeleted'));
      qc.invalidateQueries({ queryKey: qKey });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleExpand = (id: string) =>
    setExpandedKeys((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const openEdit = (e: Entry) => {
    setEditItem(e);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  };
  const openNew = () => {
    setEditItem(null);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  };

  return (
    <>
      {/* Breadcrumb */}
      <div className='flex items-center gap-3 mb-5'>
        <BackButton onClick={onBack} label='Registries' />
        <span className='text-gray-700'>/</span>
        <span className='flex items-center gap-1.5 text-sm font-medium text-white'>
          <RegIcon name={registry.icon} size={14} />
          {registry.label}
        </span>
        <Badge variant='gray'>{entries.length}</Badge>
        <div className='flex-1' />
        <div className='flex gap-2'>
          <Button variant='primary' onClick={openNew}>
            <Plus size={14} className='mr-1' />{t('catalog.newEntry')}
          </Button>
        </div>
      </div>

      {/* Registry info */}
      <div className='flex items-center gap-3 mb-4 flex-wrap text-xs text-gray-500'>
        <span>
          {t('catalog.readLabel')} <AclBadge acl={registry.acl_read} />
        </span>
        <span>
          {t('catalog.writeLabel')} <AclBadge acl={registry.acl_write} />
        </span>
        <span>
          TTL: <TtlBadge ttl={registry.default_ttl} />
        </span>
        {registry.mqtt_retain && <Badge variant='purple'>MQTT retain</Badge>}
      </div>

      {/* Entries */}
      {!entries.length
        ? (
          <EmptyState
            icon={<Database size={28} className='text-gray-600' />}
            title={t('catalog.noEntries')}
            description={t('catalog.noEntriesDesc')}
            action={
              <Button variant='primary' onClick={openNew}>
                <Plus size={14} className='mr-1' />{t('catalog.newEntry')}
              </Button>
            }
          />
        )
        : (
          <div className='space-y-1.5'>
            {entries.map((e) => {
              const expanded = expandedKeys.has(e.id);
              const expiry = fmtExpiry(e.expires_at);
              const isExpiring = e.expires_at &&
                new Date(e.expires_at).getTime() - Date.now() < 60_000;

              return (
                <Card key={e.id} className='group'>
                  <CardContent className='p-0'>
                    <button
                      className='w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-800/30 transition-colors'
                      onClick={() => toggleExpand(e.id)}
                    >
                      {/* Key */}
                      <span className='font-mono text-sm text-blue-400 flex-shrink-0 mt-0.5'>
                        {e.key}
                      </span>

                      {/* Value preview */}
                      <span className='flex-1 text-xs text-gray-500 font-mono truncate mt-0.5'>
                        {typeof e.value === 'object' ? JSON.stringify(e.value) : String(e.value)}
                      </span>

                      {/* Meta */}
                      <div className='flex items-center gap-2 flex-shrink-0 ml-2'>
                        {e.node_scope !== 'global' && (
                          <Badge variant='blue'>
                            <Tag size={9} className='mr-0.5 inline' />
                            {e.node_scope}
                          </Badge>
                        )}
                        {expiry && (
                          <Badge variant={isExpiring ? 'yellow' : 'gray'}>
                            <Clock size={9} className='mr-0.5 inline' />
                            {expiry}
                          </Badge>
                        )}
                        {e.source && <span className='text-[10px] text-gray-600'>{e.source}</span>}
                        <InlineActions.Row
                          onEdit={(ev) => {
                            ev.stopPropagation();
                            openEdit(e);
                          }}
                          onDelete={() => del.mutate({ key: e.key, scope: e.node_scope })}
                          confirmDelete={t('catalog.deleteEntryConfirm', { key: e.key })}
                        />
                      </div>
                    </button>

                    {expanded && (
                      <div className='px-4 pb-3 border-t border-gray-800/50 bg-gray-900/30'>
                        <pre className='mt-2 text-xs text-gray-300 font-mono bg-gray-900/60 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap'>
                        {JSON.stringify(e.value, null, 2)}
                        </pre>
                        <div className='mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-gray-600'>
                          {e.source && (
                            <span>
                              {t('catalog.sourceShort')} <span className='text-gray-400'>{e.source}</span>
                            </span>
                          )}
                          {e.ttl && (
                            <span>
                              TTL: <span className='text-gray-400'>{e.ttl}s</span>
                            </span>
                          )}
                          {e.expires_at && (
                            <span>
                              {t('catalog.expiresLabel')}{' '}
                              <span className='text-gray-400'>{fmtDate(e.expires_at)}</span>
                            </span>
                          )}
                          {e.updated_at && (
                            <span>
                              {t('catalog.updatedLabel')}{' '}
                              <span className='text-gray-400'>{fmtDate(e.updated_at)}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      <EntryForm
        key={formKey}
        open={formOpen}
        initial={editItem
          ? {
            key: editItem.key,
            value: JSON.stringify(editItem.value),
            node_scope: editItem.node_scope,
            source: editItem.source,
            ttl: editItem.ttl,
          }
          : null}
        registry={registry}
        onClose={() => setFormOpen(false)}
        onSave={(d) => save.mutateAsync(d)}
      />
    </>
  );
}

// ─── ResourcesView ────────────────────────────────────────────────────────────

/** Barva stavu cesty — cesta je živý stav, ne konfigurace. */
function routeVariant(
  status: string | null,
): 'success' | 'warning' | 'error' | 'default' {
  if (!status) return 'default';
  if (status === 'ok' || status === 'online' || status === 'local') return 'success';
  if (status === 'degraded' || status === 'unknown') return 'warning';
  return 'error';
}

function AssignModal(
  { resource, tenants, onClose, onSave }: {
    resource: Resource;
    tenants: TenantOption[];
    onClose: () => void;
    onSave: (tenant: string | null) => Promise<unknown>;
  },
) {
  const { t } = useTranslation();
  const [tenant, setTenant] = useState(resource.tenant_id ?? '');
  const [saving, setSaving] = useState(false);

  return (
    <Modal
      open
      onClose={onClose}
      title={t('catalog.resAssignTitle', 'Přidělit zdroj tenantovi')}
    >
      <div className='space-y-4'>
        <div className='text-sm text-slate-300'>
          <div className='font-medium text-slate-100'>{resource.qualifier ?? resource.key}</div>
          <div className='text-xs text-slate-400 mt-1'>{resource.kind}</div>
        </div>

        <label className='block'>
          <span className='block text-xs uppercase text-slate-400 mb-1'>
            {t('catalog.resTenant', 'Tenant')}
          </span>
          <Select
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            options={[
              { value: '', label: t('catalog.resNoTenant', '— nepřiděleno —') },
              ...tenants.map((x) => ({ value: x.id, label: `${x.name} (${x.slug})` })),
            ]}
          />
        </label>

        <p className='text-xs text-slate-400'>
          {t(
            'catalog.resAssignHint',
            'Přidělení říká, kdo zdroj spravuje. Co přesně s ním smí, dává kontrakt — ne tohle přidělení. Přidělení přežije reinzerci schopnosti.',
          )}
        </p>

        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={onClose}>{t('common.cancel', 'Zrušit')}</Button>
          <Button
            variant='primary'
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(tenant || null);
                onClose();
              } finally {
                setSaving(false);
              }
            }}
          >
            {t('common.save', 'Uložit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ResourcesView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [assign, setAssign] = useState<Resource | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['catalog-resources'],
    queryFn: () =>
      api.mcp<{
        resources?: Resource[];
        caller_is_root?: boolean;
        total?: number;
        assigned?: number;
        unassigned?: number;
      }>('catalog.resources_list', {}, { silent: true }),
    refetchInterval: 30_000,
  });

  // Seznam tenantů je potřeba jen pro přidělování; když na to volající nemá
  // práva, tool selže a dropdown zůstane prázdný — panel tím nespadne.
  const { data: tenantData } = useQuery({
    queryKey: ['catalog-resources-tenants'],
    queryFn: () =>
      api.mcp<{ tenants?: TenantOption[] }>(
        'mesh.tenants',
        { action: 'list' },
        { silent: true },
      ).catch(() => ({ tenants: [] as TenantOption[] })),
    staleTime: 300_000,
  });

  const save = useMutation({
    mutationFn: ({ key, tenant }: { key: string; tenant: string | null }) =>
      api.mcp('catalog.resource_assign', { key, tenant }),
    onSuccess: () => {
      toast.success(t('catalog.resAssigned', 'Přidělení zdroje uloženo'));
      qc.invalidateQueries({ queryKey: ['catalog-resources'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resources = data?.resources ?? [];

  if (isLoading) {
    return <div className='text-sm text-slate-400'>{t('common.loading', 'Načítám…')}</div>;
  }

  if (!resources.length) {
    return (
      <EmptyState
        icon={<Globe size={28} />}
        title={t('catalog.resEmptyTitle', 'Žádné zdroje')}
        description={t(
          'catalog.resEmptyDesc',
          'Zdroj inzeruje jeho držitel (např. dns.capability_advertise). Tenant admin tady vidí zdroje svého tenanta.',
        )}
      />
    );
  }

  return (
    <div className='space-y-4'>
      <StatsGrid columns={3}>
        <StatCard
          label={t('catalog.resStatTotal', 'Zdroje')}
          value={data?.total ?? resources.length}
          icon={Globe}
        />
        <StatCard
          label={t('catalog.resStatAssigned', 'Přiděleno tenantovi')}
          value={data?.assigned ?? 0}
          icon={ShieldCheck}
          variant='success'
        />
        <StatCard
          label={t('catalog.resStatUnassigned', 'Nepřiděleno')}
          value={data?.unassigned ?? 0}
          icon={Tag}
          variant={(data?.unassigned ?? 0) > 0 ? 'warning' : 'info'}
        />
      </StatsGrid>

      <div className='overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='text-left text-xs uppercase text-slate-400 border-b border-slate-700'>
              <th className='py-2 pr-3'>{t('catalog.resColResource', 'Zdroj')}</th>
              <th className='py-2 pr-3'>{t('catalog.resColTenant', 'Tenant')}</th>
              <th className='py-2 pr-3'>{t('catalog.resColDelegated', 'Delegováno na')}</th>
              <th className='py-2 pr-3'>{t('catalog.resColRoute', 'Cesta')}</th>
              <th className='py-2 pr-3'>{t('catalog.resColScope', 'Rozsah')}</th>
              <th className='py-2' />
            </tr>
          </thead>
          <tbody>
            {resources.map((r) => (
              <tr key={r.key} className='border-b border-slate-800 align-top'>
                <td className='py-2 pr-3'>
                  <div className='font-medium text-slate-100'>{r.qualifier ?? r.key}</div>
                  <div className='text-xs text-slate-400'>
                    {r.kind}
                    {r.provider_type ? ` · ${r.provider_type}` : ''}
                    {r.primary ? ' · primary' : ''}
                  </div>
                </td>
                <td className='py-2 pr-3'>
                  {r.assigned
                    ? (
                      <span className='text-slate-200'>
                        {r.tenant_name ?? r.tenant_slug}
                      </span>
                    )
                    : (
                      <Badge variant='warning'>
                        {t('catalog.resNoTenantShort', 'nepřiděleno')}
                      </Badge>
                    )}
                </td>
                <td className='py-2 pr-3'>
                  {/* UUID je identita na drátě; label je jen popisek a u cizího uzlu chybí. */}
                  <div className='text-slate-200'>
                    {r.delegated_to_label ??
                      (r.delegated_to_is_self
                        ? t('catalog.resSelf', 'tento uzel')
                        : t('catalog.resUnknownNode', 'neznámý uzel'))}
                  </div>
                  <div className='text-xs font-mono text-slate-500 break-all'>
                    {r.delegated_to ?? '—'}
                  </div>
                </td>
                <td className='py-2 pr-3'>
                  <Badge variant={routeVariant(r.route_status)}>
                    {r.route_status ?? t('catalog.resNoRoute', 'bez cesty')}
                  </Badge>
                  <div className='text-xs text-slate-400 mt-1'>
                    {r.route_transport ?? ''}
                    {r.route_via ? ` · via ${r.route_via.slice(0, 8)}` : ''}
                  </div>
                </td>
                <td className='py-2 pr-3'>
                  {r.contracts.length
                    ? (
                      <div className='space-y-1'>
                        {r.contracts.map((c) => (
                          <div key={c.key} className='text-xs font-mono text-slate-300 break-all'>
                            {c.key}
                          </div>
                        ))}
                      </div>
                    )
                    : (
                      <span className='text-xs text-slate-500'>
                        {t('catalog.resNoContract', 'bez kontraktu')}
                      </span>
                    )}
                </td>
                <td className='py-2'>
                  <InlineActions.Row visible>
                    <Button variant='ghost' onClick={() => setAssign(r)}>
                      {t('catalog.resAssign', 'Přidělit')}
                    </Button>
                  </InlineActions.Row>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {assign && (
        <AssignModal
          resource={assign}
          tenants={tenantData?.tenants ?? []}
          onClose={() => setAssign(null)}
          onSave={(tenant) => save.mutateAsync({ key: assign.key, tenant })}
        />
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CatalogPage() {
  const { t } = useTranslation();
  const [view, setView] = useState<View>({ type: 'registries' });

  const { data } = useQuery({
    queryKey: ['catalog-registries'],
    queryFn: () =>
      api.mcp<{ registries?: Registry[] }>('catalog.list', {}, { silent: true }),
    refetchInterval: 30_000,
  });

  const registries: Registry[] = data?.registries ?? [];
  const totalEntries = registries.reduce((s, r) => s + parseInt(r.entry_count || '0'), 0);
  const mqttRegistries = registries.filter((r) => r.mqtt_retain).length;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Catalog'
        description={t('catalog.pageDescription')}
        icon={<Database size={20} />}
      />

      <StatsGrid columns={4}>
        <StatCard label={t('catalog.statRegistries')} value={registries.length} icon={Layers} />
        <StatCard label='Entries' value={totalEntries} icon={Database} variant='info' />
        <StatCard label='MQTT retain' value={mqttRegistries} icon={Network} variant='success' />
        <StatCard
          label={t('catalog.statWithTtl')}
          value={registries.filter((r) => r.default_ttl).length}
          icon={Clock}
          variant='warning'
        />
      </StatsGrid>

      <Tabs defaultTab='registries'>
        <TabList>
          <Tab id='registries'>
            <Layers size={14} className='inline mr-1' />
            {t('catalog.tabRegistries', 'Registry')}
          </Tab>
          <Tab id='resources'>
            <Globe size={14} className='inline mr-1' />
            {t('catalog.tabResources', 'Zdroje')}
          </Tab>
        </TabList>

        <TabPanel id='registries'>
          <Card>
            <CardContent className='pt-5'>
              {view.type === 'registries' && (
                <RegistriesView
                  registries={registries}
                  onSelect={(r) => setView({ type: 'entries', registry: r })}
                  onRefresh={() => {}}
                />
              )}
              {view.type === 'entries' && (
                <EntriesView
                  registry={view.registry}
                  onBack={() => setView({ type: 'registries' })}
                />
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel id='resources'>
          <Card>
            <CardContent className='pt-5'>
              <ResourcesView />
            </CardContent>
          </Card>
        </TabPanel>
      </Tabs>
    </div>
  );
}
